import { createClient } from '@supabase/supabase-js';
import { Database } from './database.types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Create a single instance with optimized settings
export const supabase = createClient<Database>(supabaseUrl, supabaseKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storageKey: 'app-auth',
    storage: localStorage
  },
  db: {
    schema: 'public'
  },
  realtime: {
    params: {
      eventsPerSecond: 2
    }
  },
  global: {
    headers: {
      'Cache-Control': 'no-store'
    }
  }
});

// Cache storage for data with improved memory management
const cache = new Map<string, { data: any; timestamp: number; controller?: AbortController }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

// Document visibility state tracking
let isDocumentVisible = true;
document.addEventListener('visibilitychange', () => {
  isDocumentVisible = document.visibilityState === 'visible';
  if (!isDocumentVisible) {
    // Abort pending requests when page becomes hidden
    for (const entry of cache.values()) {
      if (entry.controller) {
        entry.controller.abort();
      }
    }
  }
});

// Fetch data with caching and visibility awareness
export async function fetchWithCache(key: string, fetchFn: () => Promise<any>) {
  const now = Date.now();
  const cached = cache.get(key);

  // Return cached data if valid and page is not visible
  if (cached && now - cached.timestamp < CACHE_DURATION) {
    if (!isDocumentVisible) {
      return cached.data;
    }
    // If page is visible, allow cache but don't block new requests
    if (now - cached.timestamp < CACHE_DURATION / 2) {
      return cached.data;
    }
  }

  // Create new abort controller for this request
  const controller = new AbortController();
  
  try {
    const data = await Promise.race([
      fetchFn(),
      new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout')), 10000);
      })
    ]);

    cache.set(key, { 
      data, 
      timestamp: now,
      controller 
    });
    
    return data;
  } catch (error) {
    // If we have cached data and encounter an error, return cached data
    if (cached) {
      return cached.data;
    }
    throw error;
  } finally {
    // Clean up old cache entries
    for (const [k, v] of cache.entries()) {
      if (now - v.timestamp > CACHE_DURATION) {
        if (v.controller) {
          v.controller.abort();
        }
        cache.delete(k);
      }
    }
  }
}

// Clear cache with proper cleanup
export function clearCache() {
  for (const entry of cache.values()) {
    if (entry.controller) {
      entry.controller.abort();
    }
  }
  cache.clear();
}

// Fetch data with relations and improved error handling
export async function fetchDataWithRelations(table: string, columns: string) {
  const controller = new AbortController();
  
  try {
    const { data, error } = await supabase
      .from(table)
      .select(columns)
      .abortSignal(controller.signal);

    if (error) {
      console.error(`Error fetching ${table}:`, error);
      throw error;
    }

    return data || [];
  } catch (error) {
    if (error.name === 'AbortError') {
      console.log(`Request for ${table} was aborted`);
      return [];
    }
    console.error(`Error in fetchDataWithRelations for ${table}:`, error);
    throw error;
  } finally {
    controller.abort(); // Clean up
  }
}